// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
import React, { memo, useEffect, useState } from 'react';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';

import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { ErrorBoundary } from '../../../common/ui/error-boundary';
import { localStore } from '../../../common/utils/local-store';
import { AiBrand, DEFAULT_GAS_CHANNEL } from '../../../constants';

import { AddPinCard } from './add-pin-card';
import { onAddPinDismissClick } from './analytics';
import { useAdminPermissions } from './bookmark-api/useAdminPermissions';
import { BookmarkState } from './bookmark-api/useBookmarkQuery/fetch-bookmark';
import { BookmarkKnowledgeCard } from './bookmark-knowledge-card';

const DISMISS_STATUS_KEY = 'add-new-pin-card-dismissed';

interface BookmarkProps {
	query: string;
	cloudId: string;
	orgId: string;
	bookmarkId: string;
	url: string;
	title: string;
	description: string;
	bookmarkState?: BookmarkState;
	hasBookmarkCreationAccess?: boolean;
	hasAdminhubAccess?: boolean;
	searchSessionId?: string;
	source?: string;
	brand?: AiBrand;
	userDetails?: {
		name?: string;
		id?: string;
	};
	product?: string;
	workspaceId: string;
	lastModifierAccountId: string;
	keyPhrase: string;
	parentAdminhubBookmarkId: string;
	requestId: string | null;
}

type ViewType = 'add' | 'view' | 'none';

export const Bookmark = memo(
	({
		query,
		cloudId,
		orgId,
		bookmarkId,
		bookmarkState,
		url,
		title,
		description,
		hasBookmarkCreationAccess = false,
		hasAdminhubAccess = false,
		searchSessionId = '',
		source = '',
		brand = AiBrand.AI,
		userDetails,
		product,
		workspaceId,
		lastModifierAccountId,
		keyPhrase,
		parentAdminhubBookmarkId,
		requestId,
	}: BookmarkProps): React.JSX.Element => {
		const [viewType, setViewType] = useState<ViewType>('none');
		const [fireAnalyticsEvent] = useAnalytics();
		useAdminPermissions({ cloudId, orgId });
		// hide the card when a new pin is created via AddPinCard
		const onNewPinCreated = () => setViewType('none');

		// handle dismiss CTA clicked on add new pin card
		const onDismissCallback = () => {
			fireAnalyticsEvent(onAddPinDismissClick());
			localStore.setString(DISMISS_STATUS_KEY, 'true');
			setViewType('none');
		};

		const hasBookmarkCreationAccessWithFg =
			hasBookmarkCreationAccess && fg('adminhub_search_pins_settings');

		useEffect(() => {
			if (url) {
				// show the bookmark card when a url exists
				setViewType('view');
			} else if (
				!url &&
				localStore.getString(DISMISS_STATUS_KEY) !== 'true' &&
				hasBookmarkCreationAccessWithFg
			) {
				// show the add bookmark card if a user is some kind of admin & bookmark for this query doesn't exist
				setViewType('add');
			}
		}, [hasBookmarkCreationAccessWithFg, url]);

		const commonBookmarkProps = {
			query,
			bookmarkState,
			url,
			bookmarkId,
			orgId,
			cloudId,
			description,
			searchSessionId,
			source,
			title,
			hasBookmarkCreationAccess: hasBookmarkCreationAccessWithFg,
			hasAdminhubAccess,
			brand,
			userDetails,
			workspaceId,
			product: product ?? '',
			lastModifierAccountId,
			keyPhrase,
			parentAdminhubBookmarkId,
			requestId,
		};

		return (
			<ErrorBoundary fallback={<></>}>
				<FlagsProvider>
					<AIEventsInstrumentationProvider
						config={{
							product: product ?? 'unknown',
							subproduct: 'search-page',
							aiFeatureName: 'smartAnswers',
							aiExperienceName: 'bookmarks',
							proactiveGeneratedAI: bookmarkState === BookmarkState.ACTIVE ? 0 : 1,
							userGeneratedAI: bookmarkState === BookmarkState.ACTIVE ? 1 : 0,
							isAIFeature: 1,
							source: source,
							channel: DEFAULT_GAS_CHANNEL,
						}}
					>
						{viewType === 'view' ? (
							<BookmarkKnowledgeCard {...commonBookmarkProps} />
						) : viewType === 'add' ? (
							<AddPinCard
								query={query}
								orgId={orgId}
								cloudId={cloudId}
								hasAdminhubAccess={hasAdminhubAccess}
								callbacks={{
									onSuccessCallback: onNewPinCreated,
									onDismissCallback,
								}}
								workspaceId={workspaceId}
								product={product ?? ''}
							/>
						) : (
							<></>
						)}
					</AIEventsInstrumentationProvider>
				</FlagsProvider>
			</ErrorBoundary>
		);
	},
);

export { BookmarkSuggestionCard } from './bookmark-suggestion-card';
